import React, { useState, useRef, useEffect } from "react";
import "./CalendarPicker.scss";

// businessHours is indexed 0=Sunday..6=Saturday to match the rest of the app
// (see weekday-from-Luxon normalization in SearchSlotStep.tsx).
const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

interface CalendarPickerProps {
  minDate: string;       // ISO date string (yyyy-MM-dd)
  maxDate: string;       // ISO date string (yyyy-MM-dd)
  businessHours?: Array<{ closed: boolean; begin?: string; end?: string }>;
  shifts?: Array<{ date: string; begin?: string; end?: string }>;
  timezone?: string;
  selectedDate: string | null;
  onSelectDate: (date: string) => void;
}

/**
 * Check if a given ISO date (yyyy-MM-dd) is closed based on business hours.
 */
export function isDayClosed(
  dateStr: string,
  businessHours?: Array<{ closed: boolean; begin?: string; end?: string }>,
  shifts?: Array<{ date: string; begin?: string; end?: string }>
): boolean {
  // Check shifts first (date-specific overrides)
  if (shifts && shifts.length > 0) {
    for (const shift of shifts) {
      if (shift.date === dateStr) {
        return shift.begin == null || shift.end == null;
      }
    }
  }

  // Check weekly business hours
  if (businessHours && businessHours.length > 0) {
    const dt = new Date(dateStr + "T00:00:00");
    const dayOfWeek = dt.getDay(); // 0=Sun, 6=Sat
    const daySchedule = businessHours[dayOfWeek];
    if (daySchedule && daySchedule.closed) {
      return true;
    }
  }

  return false;
}

const CalendarPicker: React.FC<CalendarPickerProps> = ({
  minDate,
  maxDate,
  businessHours,
  shifts,
  selectedDate,
  onSelectDate,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [viewYear, setViewYear] = useState<number>(() => {
    if (selectedDate) return new Date(selectedDate + "T00:00:00").getFullYear();
    return new Date().getFullYear();
  });
  const [viewMonth, setViewMonth] = useState<number>(() => {
    if (selectedDate) return new Date(selectedDate + "T00:00:00").getMonth();
    return new Date().getMonth();
  });

  const pickerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Keep the visible month in sync with externally driven changes to selectedDate
  // (e.g. parent auto-advances the date when no times are available on the chosen day).
  useEffect(() => {
    if (!selectedDate) return;
    const dt = new Date(selectedDate + "T00:00:00");
    setViewYear(dt.getFullYear());
    setViewMonth(dt.getMonth());
  }, [selectedDate]);

  // Close on outside click / tap
  useEffect(() => {
    if (!isOpen) return;

    function handleClickOutside(event: MouseEvent | TouchEvent) {
      if (
        pickerRef.current &&
        !pickerRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside, { passive: true });
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") setIsOpen(false);
    }
    if (isOpen) document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  const minDateObj = new Date(minDate + "T00:00:00");
  const maxDateObj = new Date(maxDate + "T00:00:00");

  // Build the calendar grid for the current view month
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const firstDayOfWeek = new Date(viewYear, viewMonth, 1).getDay();

  // Previous month padding days
  const prevMonthDays: { day: number; outside: true }[] = [];
  if (firstDayOfWeek > 0) {
    const prevMonthLastDate = new Date(viewYear, viewMonth, 0).getDate();
    for (let i = firstDayOfWeek - 1; i >= 0; i--) {
      prevMonthDays.push({ day: prevMonthLastDate - i, outside: true });
    }
  }

  // Current month days
  const currentMonthDays: { day: number; dateStr: string; outside: false }[] = [];
  for (let d = 1; d <= daysInMonth; d++) {
    const y = viewYear;
    const m = String(viewMonth + 1).padStart(2, "0");
    const dayStr = String(d).padStart(2, "0");
    currentMonthDays.push({ day: d, dateStr: `${y}-${m}-${dayStr}`, outside: false });
  }

  // Next month padding days to fill the last row (7 columns)
  const totalCells = prevMonthDays.length + currentMonthDays.length;
  const remainingCells = (7 - (totalCells % 7)) % 7;
  const nextMonthDays: { day: number; outside: true }[] = [];
  for (let d = 1; d <= remainingCells; d++) {
    nextMonthDays.push({ day: d, outside: true });
  }

  // Combine all cells
  type Cell =
    | { day: number; dateStr?: string; outside: true }
    | { day: number; dateStr: string; outside: false };

  const allCells: Cell[] = [...prevMonthDays, ...currentMonthDays, ...nextMonthDays];

  // Determine if a cell is selectable
  function isSelectable(dateStr: string): boolean {
    const dt = new Date(dateStr + "T00:00:00");
    if (dt < minDateObj || dt > maxDateObj) return false;
    if (isDayClosed(dateStr, businessHours, shifts)) return false;
    return true;
  }

  function isToday(dateStr: string): boolean {
    const today = new Date();
    const y = today.getFullYear();
    const m = String(today.getMonth() + 1).padStart(2, "0");
    const d = String(today.getDate()).padStart(2, "0");
    return dateStr === `${y}-${m}-${d}`;
  }

  function isSelected(dateStr: string): boolean {
    return selectedDate === dateStr;
  }

  // Navigation
  function goPrevMonth() {
    if (viewMonth === 0) {
      setViewYear(y => y - 1);
      setViewMonth(11);
    } else {
      setViewMonth(m => m - 1);
    }
  }

  function goNextMonth() {
    if (viewMonth === 11) {
      setViewYear(y => y + 1);
      setViewMonth(0);
    } else {
      setViewMonth(m => m + 1);
    }
  }

  // Touch-friendly tap handler for prev/next
  function handlePrevTap(e: React.TouchEvent | React.MouseEvent) {
    e.preventDefault();
    goPrevMonth();
  }

  function handleNextTap(e: React.TouchEvent | React.MouseEvent) {
    e.preventDefault();
    goNextMonth();
  }

  // Build the display text for the trigger button
  const displayText = selectedDate
    ? (() => {
        const dt = new Date(selectedDate + "T00:00:00");
        return `${MONTH_NAMES[dt.getMonth()]} ${dt.getDate()}, ${dt.getFullYear()}`;
      })()
    : "Select date";

  // Render a single cell
  function renderCell(cell: Cell) {
    if (cell.outside) {
      return <td key={`p-${cell.day}`} className="cal-day cal-day--empty" />;
    }

    const selectable = isSelectable(cell.dateStr!);
    const today = isToday(cell.dateStr!);
    const selected = isSelected(cell.dateStr!);

    let cls = "cal-day";
    if (today) cls += " cal-day--today";
    if (selected) cls += " cal-day--selected";
    if (!selectable) cls += " cal-day--disabled";

    return (
      <td key={cell.dateStr} className={cls}>
        <button
          type="button"
          disabled={!selectable}
          onClick={() => {
            if (!selectable) return;
            onSelectDate(cell.dateStr!);
            setIsOpen(false);
          }}
          aria-label={`${MONTH_NAMES[new Date(cell.dateStr! + "T00:00:00").getMonth()]} ${new Date(cell.dateStr! + "T00:00:00").getDate()}, ${new Date(cell.dateStr! + "T00:00:00").getFullYear()}`}
          className="cal-day-btn"
        >
          {cell.day}
        </button>
      </td>
    );
  }

  return (
    <div className="calendar-picker">
      {/* Trigger button */}
      <button
        ref={triggerRef}
        type="button"
        className="cal-trigger"
        onClick={() => setIsOpen(!isOpen)}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
      >
        <span className="cal-trigger-icon" aria-hidden="true">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="1.5" y="3" width="13" height="11.5" rx="1.5" stroke="currentColor" strokeWidth="1.2" />
            <path d="M1.5 6.5h13" stroke="currentColor" strokeWidth="1.2" />
            <path d="M5 1.5v3M11 1.5v3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
          </svg>
        </span>
        <span className="cal-trigger-text">{displayText}</span>
      </button>

      {/* Calendar popup */}
      {isOpen && (
        <>
          <div className="cal-backdrop" aria-hidden="true" onClick={() => setIsOpen(false)} />
        <div ref={pickerRef} className="cal-picker" role="dialog" aria-label="Select a date">
          {/* Header: month/year + navigation */}
          <div className="cal-header">
            <button type="button" className="cal-nav-btn cal-nav--prev" onClick={handlePrevTap}>
              ‹
            </button>
            <span className="cal-month-year">
              {MONTH_NAMES[viewMonth]} {viewYear}
            </span>
            <button type="button" className="cal-nav-btn cal-nav--next" onClick={handleNextTap}>
              ›
            </button>
          </div>

          {/* Day names row */}
          <div className="cal-weekdays">
            {DAY_NAMES.map(d => (
              <div key={d} className="cal-weekday">{d}</div>
            ))}
          </div>

          {/* Date grid */}
          <table className="cal-grid">
            <tbody>
              {(() => {
                const rows: React.ReactNode[] = [];
                for (let i = 0; i < allCells.length; i += 7) {
                  rows.push(
                    <tr key={i}>
                      {allCells.slice(i, i + 7).map(renderCell)}
                    </tr>
                  );
                }
                return rows;
              })()}
            </tbody>
          </table>
        </div>
        </>
      )}
    </div>
  );
};

export default CalendarPicker;
